import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, ButtonGroup, Card, CardBody, FormControl, FormLabel, HStack, IconButton, Image, Input, Menu, MenuButton,
  MenuItem, MenuList, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Select, Text, Tooltip, VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, CopyIcon, DeleteIcon } from '@chakra-ui/icons';
import { EmailBlock, EmailButtonTarget, EmailColourSlot } from '../../types/api';
import { BUTTON_TARGETS, MergeFieldInfo } from '../../utils/emailEditor';
import {
  addLimit, applyChipFormat, BLOCK_HINTS, BLOCK_LABELS, BlockType, chipFmtOf, chipsInRange, COLOUR_SLOTS, LINE_THICKNESSES, LineThickness, SIZE_TOOLTIP, duplicateBlock, fromEditorHtml, insertBlock, moveBlock, newBlock,
  removeBlock, textLength, toEditorHtml, MAX_TEXT_CHARS, TEXT_SIZES,
} from '../../utils/emailBlocks';

type TextBlock = Extract<EmailBlock, { type: 'text' }>;

// --- Text block: bold, italic, palette colour, link, alignment, merge-field chips ---

const TextBlockEditor: React.FC<{
  block: TextBlock;
  fields: MergeFieldInfo[];
  onChange: (b: TextBlock) => void;
  testId: string;
}> = ({ block, fields, onChange, testId }) => {
  const ref = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const lastEmitted = useRef<string>(JSON.stringify(block.paragraphs));
  const [linkUrl, setLinkUrl] = useState('https://');
  const linkPopover = useDisclosure();

  // Load from outside changes only (reset, reorder, switch email), never while typing (would move the caret).
  useEffect(() => {
    if (!ref.current) return;
    const stored = JSON.stringify(block.paragraphs);
    if (stored !== lastEmitted.current || ref.current.innerHTML === '') {
      ref.current.innerHTML = toEditorHtml(block.paragraphs, fields);
      lastEmitted.current = stored;
    }
  }, [block.paragraphs, fields]);

  const emit = () => {
    if (!ref.current) return;
    const paragraphs = fromEditorHtml(ref.current.innerHTML, fields);
    lastEmitted.current = JSON.stringify(paragraphs);
    onChange({ ...block, paragraphs });
  };
  const remember = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) savedRange.current = sel.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };
  // #31: merge-field chips are not editable, so the browser can't be trusted to format them. The chips in the
  // selection carry their own formatting (see applyChipFormat); text around them still goes through execCommand.
  const exec = (name: string, value?: string) => {
    restore();
    const sel = window.getSelection();
    const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    const chips = ref.current && range ? chipsInRange(ref.current, range) : [];
    const kind = name === 'bold' ? 'bold' : name === 'italic' ? 'italic' : name === 'foreColor' ? 'colour' : null;
    let chipValue: boolean | EmailColourSlot | null = null;
    if (kind === 'bold' || kind === 'italic') chipValue = !chips.every((c) => !!chipFmtOf(c)[kind]);
    else if (kind === 'colour') chipValue = COLOUR_SLOTS.find((c) => c.hex.toLowerCase() === (value || '').toLowerCase())?.slot || null;
    const hasText = !!range && !range.collapsed && (range.cloneContents().textContent || '').replace(/\u00a0/g, ' ').trim() !== (chips.map((c) => c.textContent || '').join('')).trim();
    if (!chips.length || hasText || (range && range.collapsed)) document.execCommand(name, false, value);
    if (kind && chips.length) applyChipFormat(chips, kind, chipValue);
    emit();
  };
  const applyLink = () => {
    if (!/^(https:\/\/|mailto:)\S{3,}$/i.test(linkUrl)) return;
    exec('createLink', linkUrl);
    linkPopover.onClose();
  };
  const insertField = (f: MergeFieldInfo) => {
    restore();
    const sel = window.getSelection();
    const editor = ref.current;
    if (!sel || !editor) return;
    let range = sel.rangeCount ? sel.getRangeAt(0) : null;
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor.lastElementChild || editor);
      range.collapse(false);
    }
    const holder = document.createElement('span');
    holder.innerHTML = toEditorHtml([[{ text: '', field: f.key }]], [f]).replace(/^<div>|<\/div>$/g, '');
    const chip = holder.firstChild as Node;
    const space = document.createTextNode('\u00a0');
    range.deleteContents();
    range.insertNode(space);
    range.insertNode(chip);
    range.setStartAfter(space);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    emit();
  };
  const onPaste = (e: React.ClipboardEvent) => {
    // Paste is cleaned: plain text only, no styles ever enter the editor.
    e.preventDefault();
    insertLines(e.clipboardData.getData('text/plain'));
  };
  // #27: a new line in a text block is ALWAYS a line break (never a paragraph); several lines pasted
  // become line breaks too. For more space, add a Spacing block or another text block.
  const insertLines = (text: string) => {
    text.replace(/\r\n?/g, '\n').split('\n').forEach((line, i) => {
      if (i) document.execCommand('insertLineBreak');
      if (line) document.execCommand('insertText', false, line);
    });
    emit();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault(); // Enter and Shift+Enter do the same thing
    document.execCommand('insertLineBreak');
    emit();
  };

  const noFocusLoss = (e: React.MouseEvent) => e.preventDefault();
  const len = textLength(block.paragraphs);

  return (
    <Box data-testid={testId}>
      <HStack mb={1} spacing={2} wrap="wrap">
        <ButtonGroup size="xs" isAttached variant="outline">
          <Button aria-label="Gras" fontWeight="bold" onMouseDown={noFocusLoss} onClick={() => exec('bold')}>G</Button>
          <Button aria-label="Italique" fontStyle="italic" onMouseDown={noFocusLoss} onClick={() => exec('italic')}>I</Button>
        </ButtonGroup>
        <Menu>
          <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} onMouseDown={remember} aria-label="Couleur">Couleur</MenuButton>
          <MenuList>
            {COLOUR_SLOTS.map((c) => (
              <MenuItem key={c.slot} fontSize="sm" onClick={() => exec('foreColor', c.hex)}>
                <Box w="14px" h="14px" borderRadius="sm" bg={c.hex} mr={2} />{c.label}
              </MenuItem>
            ))}
            <MenuItem fontSize="sm" onClick={() => exec('foreColor', '#333333')}>Couleur du texte (par défaut)</MenuItem>
          </MenuList>
        </Menu>
        <ButtonGroup size="xs" isAttached variant="outline">
          <Button aria-label="Aligner à gauche" isActive={block.align === 'left'} onClick={() => onChange({ ...block, align: 'left' })}>Gauche</Button>
          <Button aria-label="Centrer" isActive={block.align === 'center'} onClick={() => onChange({ ...block, align: 'center' })}>Centré</Button>
          <Button aria-label="Aligner à droite" isActive={block.align === 'right'} onClick={() => onChange({ ...block, align: 'right' })}>Droit</Button>
        </ButtonGroup>
        <Tooltip label={SIZE_TOOLTIP} hasArrow>
          <ButtonGroup size="xs" isAttached variant="outline" aria-label="Taille du texte">
            {TEXT_SIZES.map((s) => (
              <Button key={s.value} aria-label={'Taille ' + s.label} isActive={(block.size || 'normal') === s.value} onClick={() => onChange({ ...block, size: s.value })}>{s.label}</Button>
            ))}
          </ButtonGroup>
        </Tooltip>
        <Popover isOpen={linkPopover.isOpen} onClose={linkPopover.onClose} onOpen={() => { remember(); linkPopover.onOpen(); }}>
          <PopoverTrigger>
            <Button size="xs" variant="outline" aria-label="Lien" onMouseDown={remember}>Lien</Button>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody>
              <HStack>
                <Input size="sm" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" />
                <Button size="sm" colorScheme="blue" onClick={applyLink}>OK</Button>
              </HStack>
              <Box fontSize="xs" color="gray.500" mt={1}>Sélectionnez d'abord le texte à lier. Adresses https:// ou mailto: uniquement.</Box>
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <Menu>
          <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} onMouseDown={remember}>Insérer un champ</MenuButton>
          <MenuList>
            {fields.map((f) => <MenuItem key={f.key} fontSize="sm" onClick={() => insertField(f)}>{f.label}</MenuItem>)}
          </MenuList>
        </Menu>
      </HStack>
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Texte"
        onInput={emit}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onKeyUp={remember}
        onMouseUp={remember}
        onBlur={() => { remember(); emit(); }}
        minH="70px"
        p={3}
        borderWidth="1px"
        borderRadius="md"
        bg="white"
        fontSize={(TEXT_SIZES.find((s) => s.value === (block.size || 'normal')) || TEXT_SIZES[1]).px + 'px'}
        lineHeight={(TEXT_SIZES.find((s) => s.value === (block.size || 'normal')) || TEXT_SIZES[1]).lineHeightPx + 'px'}
        textAlign={block.align}
        _focus={{ outline: 'none', borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299e1' }}
        sx={{ a: { color: '#3182ce', textDecoration: 'underline' } }}
      />
      <Text fontSize="xs" color={len > MAX_TEXT_CHARS ? 'red.500' : 'gray.500'} mt={1} textAlign="right">
        {len} / {MAX_TEXT_CHARS}
      </Text>
    </Box>
  );
};

// --- The other block types ---

const ColourSelect: React.FC<{ value: EmailColourSlot; onChange: (c: EmailColourSlot) => void }> = ({ value, onChange }) => (
  <HStack>
    <Box w="20px" h="20px" borderRadius="sm" borderWidth="1px" bg={COLOUR_SLOTS.find((c) => c.slot === value)?.hex} flexShrink={0} />
    <Select size="sm" bg="white" value={value} onChange={(e) => onChange(e.target.value as EmailColourSlot)} aria-label="Couleur">
      {COLOUR_SLOTS.map((c) => <option key={c.slot} value={c.slot}>{c.label}</option>)}
    </Select>
  </HStack>
);

const BlockBody: React.FC<{ block: EmailBlock; fields: MergeFieldInfo[]; onChange: (b: EmailBlock) => void; testId: string }> = ({
  block, fields, onChange, testId,
}) => {
  switch (block.type) {
    case 'text':
      return <TextBlockEditor block={block} fields={fields} onChange={onChange} testId={testId + '-text'} />;
    case 'line':
      return (
        <VStack align="stretch" spacing={3}>
          <HStack align="flex-end" spacing={3} wrap="wrap">
            <FormControl w="auto" flex="1" minW="160px">
              <FormLabel fontSize="sm" mb={1}>Couleur</FormLabel>
              <ColourSelect value={block.colour || 'primary'} onChange={(colour) => onChange({ ...block, colour })} />
            </FormControl>
            <FormControl w="auto" flex="1" minW="160px">
              <FormLabel fontSize="sm" mb={1}>Épaisseur</FormLabel>
              <Select size="sm" bg="white" value={block.thickness || 'thin'} aria-label="Épaisseur"
                onChange={(e) => onChange({ ...block, thickness: e.target.value as LineThickness })}>
                {LINE_THICKNESSES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </Select>
            </FormControl>
          </HStack>
          <Box data-testid={testId + '-line-preview'} h={(LINE_THICKNESSES.find((x) => x.value === (block.thickness || 'thin')) || LINE_THICKNESSES[0]).px + 'px'}
            bg={COLOUR_SLOTS.find((c) => c.slot === (block.colour || 'primary'))?.hex} my={1} />
        </VStack>
      );
    case 'spacing':
      return (
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>Hauteur</FormLabel>
          <Select size="sm" bg="white" maxW="200px" value={block.size} onChange={(e) => onChange({ ...block, size: e.target.value as 'small' | 'medium' | 'large' })} aria-label="Hauteur">
            <option value="small">Petit</option>
            <option value="medium">Moyen</option>
            <option value="large">Grand</option>
          </Select>
        </FormControl>
      );
    case 'button':
      return (
        <VStack align="stretch" spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>Libellé</FormLabel>
            <Input size="sm" bg="white" value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })} aria-label="Libellé" />
          </FormControl>
          <HStack align="flex-end" spacing={3} wrap="wrap">
            <FormControl w="auto" flex="1" minW="160px">
              <FormLabel fontSize="sm" mb={1}>Couleur</FormLabel>
              <ColourSelect value={block.colour} onChange={(colour) => onChange({ ...block, colour })} />
            </FormControl>
            <FormControl w="auto" flex="1" minW="160px">
              <FormLabel fontSize="sm" mb={1}>Le bouton ouvre</FormLabel>
              <Select size="sm" bg="white" value={block.target} aria-label="Le bouton ouvre"
                onChange={(e) => onChange({ ...block, target: e.target.value as EmailButtonTarget })}>
                {BUTTON_TARGETS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </Select>
            </FormControl>
          </HStack>
          {block.target === 'external' && (
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>Adresse du lien (https://)</FormLabel>
              <Input size="sm" bg="white" value={block.url || ''} placeholder="https://" aria-label="Adresse du lien" onChange={(e) => onChange({ ...block, url: e.target.value })} />
            </FormControl>
          )}
        </VStack>
      );
    default:
      return (
        <Text fontSize="xs" color="gray.500">
          Affiche le QR code d'accès de l'invité, centré (200 px). Ajoutez un bloc texte au-dessus pour la légende.
        </Text>
      );
  }
};

// --- The list ---

const ADDABLE: BlockType[] = ['text', 'line', 'button', 'spacing', 'qr'];

// The fixed Bandeau / Pied de page, greyed, at the top and bottom so the list reads in email order.
const FixedCard: React.FC<{ label: string; url?: string; testId: string }> = ({ label, url, testId }) => (
  <Card variant="outline" size="sm" bg="gray.50" borderStyle="dashed" data-testid={testId}>
    <CardBody>
      <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={url ? 2 : 0}>{label}</Text>
      {url && <Image src={url} alt={label} w="100%" h="auto" maxH="120px" objectFit="contain" data-testid={testId + "-thumb"} />}
    </CardBody>
  </Card>
);

const BlockEditor: React.FC<{ blocks: EmailBlock[]; fields: MergeFieldInfo[]; onChange: (b: EmailBlock[]) => void; resetKey: string | number; allowQr?: boolean; headerUrl?: string; footerUrl?: string }> = ({
  blocks, fields, onChange, resetKey, allowQr = false, headerUrl, footerUrl,
}) => {
  const set = (i: number, b: EmailBlock) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  // Stable keys per slot would remount text editors on reorder; keying by index + resetKey keeps the editor
  // in step with the data because TextBlockEditor reloads whenever its paragraphs change from outside.
  return (
    <VStack align="stretch" spacing={4} data-testid="block-editor">
      <FixedCard label="Bandeau (fixe)" url={headerUrl} testId="block-fixed-header" />
      {blocks.map((b, i) => (
        <Card key={`${resetKey}-${i}`} variant="outline" size="sm" borderLeftWidth="4px" borderLeftColor="blue.400" data-testid={`block-${i}`}>
          <HStack justify="space-between" bg="gray.50" px={3} py={1} borderBottomWidth="1px" borderTopRightRadius="md">
              <Text fontSize="sm" fontWeight="semibold" data-testid={`block-${i}-title`}>{`${i + 1} · ${BLOCK_LABELS[b.type]}`}</Text>
              <HStack spacing={1}>
                <Tooltip label="Monter"><IconButton size="xs" variant="ghost" aria-label="Monter" icon={<ArrowUpIcon />} isDisabled={i === 0} onClick={() => onChange(moveBlock(blocks, i, i - 1))} /></Tooltip>
                <Tooltip label="Descendre"><IconButton size="xs" variant="ghost" aria-label="Descendre" icon={<ArrowDownIcon />} isDisabled={i === blocks.length - 1} onClick={() => onChange(moveBlock(blocks, i, i + 1))} /></Tooltip>
                <Tooltip label={addLimit(blocks, b.type) || 'Dupliquer'}><IconButton size="xs" variant="ghost" aria-label="Dupliquer" icon={<CopyIcon />} isDisabled={!!addLimit(blocks, b.type)} onClick={() => onChange(duplicateBlock(blocks, i))} /></Tooltip>
                <Tooltip label="Supprimer"><IconButton size="xs" variant="ghost" colorScheme="red" aria-label="Supprimer" icon={<DeleteIcon />} onClick={() => onChange(removeBlock(blocks, i))} /></Tooltip>
              </HStack>
          </HStack>
          <CardBody>
            <BlockBody block={b} fields={fields} onChange={(nb) => set(i, nb)} testId={`block-${i}`} />
          </CardBody>
        </Card>
      ))}
      <FixedCard label="Pied de page (fixe)" url={footerUrl} testId="block-fixed-footer" />
      <Menu>
        <MenuButton as={Button} size="sm" variant="outline" alignSelf="flex-start" rightIcon={<ChevronDownIcon />} data-testid="block-add">
          Ajouter un bloc
        </MenuButton>
        <MenuList>
          {/* The access QR is only ever sent in the final confirmation (#23, #30): offered there only. */}
          {ADDABLE.filter((type) => type !== 'qr' || allowQr).map((type) => {
            const why = addLimit(blocks, type);
            return (
              <MenuItem key={type} fontSize="sm" isDisabled={!!why} onClick={() => onChange(insertBlock(blocks, blocks.length, newBlock(type)))} data-testid={`block-add-${type}`}>
                <Box>
                  <Text as="span">{BLOCK_LABELS[type]}{why ? ` (${why})` : ''}</Text>
                  {BLOCK_HINTS[type] && <Text fontSize="xs" color="gray.500" data-testid={`block-add-${type}-hint`}>{BLOCK_HINTS[type]}</Text>}
                </Box>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </VStack>
  );
};

export default BlockEditor;

